import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, FileChips, formatDate, DeadlineWarning, projectInfo, deadlineLabel, ServiceLists, parsePrice, trFold, SearchClear } from "@/components/shared";
import { MessageThread } from "@/components/ProjectMessages";
import { useAuth } from "@/context/AuthContext";
import { isPrivileged, hasPrivilege, getPrivilegeToken } from "@/lib/privilege";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, XCircle, Award, FileSpreadsheet, Star, Clock, Eye, BellRing, Bell, Undo2, Pencil, ShieldCheck, MessageCircleQuestion, Send, HandCoins, Search, UserPlus } from "lucide-react";
import { PriceProposalModal, ApplyProposalDialog } from "@/components/PriceProposal";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const waitDur = (iso, now) => {
  const ms = now - new Date(iso).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(1, mins)} dakikadır`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saattir`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH > 0 ? `${days} gün ${remH} saattir` : `${days} gündür`;
};

export default function CompareRequestPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const fullAdmin = !isCustomer && (user?.permissions || ["all"]).includes("all");
  const canEditPrice = fullAdmin;
  const [req, setReq] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rates, setRates] = useState(null);
  const [sortBy, setSortBy] = useState("price");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [transfers, setTransfers] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [tTo, setTTo] = useState("");
  const [tNote, setTNote] = useState("");
  const [tMode, setTMode] = useState("direct");
  const [tApprovers, setTApprovers] = useState([{ name: "", email: "" }]);
  const [tPw, setTPw] = useState("");
  const [tBusy, setTBusy] = useState(false);
  const [remindT, setRemindT] = useState(null);
  const [remindSel, setRemindSel] = useState([]);
  const [remindNote, setRemindNote] = useState("");
  const [remindBusy, setRemindBusy] = useState(false);

  const loadTransfers = () => api.get(`/requests/${id}/transfers`).then((r) => setTransfers(r.data)).catch(() => {});

  const openRemind = (t) => {
    const pend = (t.approvers || []).filter((a) => a.status === "pending").map((a) => a.email);
    setRemindT(t);
    setRemindSel(pend.length ? pend : [t.approver_email].filter(Boolean));
    setRemindNote("");
  };

  const sendRemind = async () => {
    if (!remindSel.length) return toast.error("En az bir onaylayıcı seçin");
    setRemindBusy(true);
    try {
      const r = await api.post(`/requests/${id}/transfers/${remindT.id}/remind`, { emails: remindSel, note: remindNote });
      toast.success(`${r.data.sent.length} onaylayıcıya hatırlatma gönderildi`);
      setRemindT(null);
      loadTransfers();
    } catch (e) { toast.error(apiError(e)); } finally { setRemindBusy(false); }
  };

  const submitTransfer = async () => {
    if (!tTo) return toast.error("Devredilecek firmayı seçin");
    const validApprovers = tApprovers.filter((a) => a.email.trim());
    if (tMode === "approval" && validApprovers.length === 0) return toast.error("En az bir onaylayıcı e-postası girin");
    if (!hasPrivilege() && !tPw) return toast.error("Süper şifre gerekli");
    setTBusy(true);
    try {
      const r = await api.post(`/requests/${id}/transfer`, {
        to_offer_id: tTo,
        note: tNote,
        ...(hasPrivilege() ? { privilege_token: getPrivilegeToken() } : { super_password: tPw }),
        ...(tMode === "approval" ? { approvers: validApprovers } : {}),
      });
      toast.success(r.data.status === "pending_approval"
        ? `Devir onay bağlantısı ${validApprovers.length} onaylayıcıya e-posta ile gönderildi`
        : "İhale devredildi");
      setTransferOpen(false);
      setTTo(""); setTNote(""); setTApprovers([{ name: "", email: "" }]); setTPw(""); setTMode("direct");
      load();
      loadProposals();
      loadTransfers();
    } catch (e) { toast.error(apiError(e)); } finally { setTBusy(false); }
  };
  const [note, setNote] = useState("");
  const [exporting, setExporting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [reminding, setReminding] = useState(null);
  const [remindingAll, setRemindingAll] = useState(false);
  const [priceEdit, setPriceEdit] = useState(null);
  const [actPw, setActPw] = useState("");
  const [quickQ, setQuickQ] = useState(null);
  const [qqList, setQqList] = useState([]);
  const [qqText, setQqText] = useState("");
  const [qqSending, setQqSending] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [proposalFor, setProposalFor] = useState(null);
  const [applyProposal, setApplyProposal] = useState(null);
  const [manualFor, setManualFor] = useState(null);
  const [manualPrice, setManualPrice] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualPw, setManualPw] = useState("");
  const [manualBusy, setManualBusy] = useState(false);
  const [manualNotify, setManualNotify] = useState(true);
  const [manualMsg, setManualMsg] = useState("");
  const [manualMsgDirty, setManualMsgDirty] = useState(false);
  const [manualTpl, setManualTpl] = useState(null);

  useEffect(() => {
    if (!isCustomer) api.get("/settings/manual-offer-template").then((r) => setManualTpl(r.data.text)).catch(() => {});
  }, [isCustomer]);

  const buildManualMsg = useCallback((c, p) => {
    const cur = req?.currency || "TRY";
    const tpl = manualTpl || `Sayın {firma},

{ihale} ihalesi için ihale tutarı komisyon tarafından {tutar} {para_birimi} olarak belirtilmiştir.

Aşağıdaki bağlantıdan bu tutarı kabul edip etmediğinizi (Evet / Hayır) bildirebilir veya farklı bir tutar önerebilirsiniz.

ÖNEMLİ: Malzeme detayları satır satır yazılmış olsa dahi, güncel teklif dosyalarınızı (PDF/Excel) mutlaka sisteme yüklemenizi rica ederiz.

Saygılarımızla,
GettProposal`;
    return tpl
      .replaceAll("{firma}", c.supplier.company_name)
      .replaceAll("{ihale}", req?.title || "")
      .replaceAll("{tutar}", p ? p.toLocaleString("tr-TR", { minimumFractionDigits: 2 }) : "____")
      .replaceAll("{para_birimi}", cur);
  }, [req, manualTpl]);

  useEffect(() => {
    if (!manualFor || manualMsgDirty) return;
    const p = parsePrice(manualPrice);
    setManualMsg(buildManualMsg(manualFor, p && p > 0 ? p : null));
  }, [manualFor, manualPrice, manualMsgDirty, buildManualMsg]);

  const submitManualOffer = async () => {
    const p = parsePrice(manualPrice);
    if (!p || p <= 0) return toast.error("Geçerli bir fiyat girin");
    const priv = hasPrivilege();
    if (!priv && !manualPw) return toast.error("Süper şifreyi girin");
    setManualBusy(true);
    try {
      const payload = { supplier_id: manualFor.supplier.id, price: p, note: manualNote, notify: manualNotify, message: manualNotify ? manualMsg : "" };
      if (priv) payload.privilege_token = getPrivilegeToken();
      else payload.super_password = manualPw;
      await api.post(`/requests/${id}/manual-offer`, payload);
      toast.success(manualNotify ? `${manualFor.supplier.company_name} için teklif kaydedildi ve onay e-postası gönderildi` : `${manualFor.supplier.company_name} için teklif kaydedildi`);
      setManualFor(null);
      load();
      loadProposals();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setManualBusy(false);
    }
  };

  const loadProposals = useCallback(() => {
    if (isCustomer) return;
    api.get(`/price-proposals?request_id=${id}`).then((r) => setProposals(r.data)).catch(() => {});
    api.get(`/requests/${id}/transfers`).then((r) => setTransfers(r.data)).catch(() => {});
  }, [id, isCustomer]);
  useEffect(() => { loadProposals(); }, [loadProposals]);
  const proposalByOffer = useMemo(() => {
    const m = {};
    for (const p of proposals) if (!m[p.offer_id]) m[p.offer_id] = p;
    return m;
  }, [proposals]);

  const openQuickQ = (c) => {
    setQuickQ(c);
    setQqText("");
    if (qqList.length === 0) api.get("/quick-questions").then((r) => setQqList(r.data)).catch(() => {});
  };

  const sendQuickQ = async (text) => {
    if (!text.trim()) return;
    setQqSending(true);
    try {
      await api.post(`/requests/${id}/messages/${quickQ.supplier.id}`, { text: text.trim(), file_ids: [] });
      toast.success(`Soru ${quickQ.supplier.company_name} firmasına gönderildi`);
      setQuickQ(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setQqSending(false);
    }
  };
  const [newPrice, setNewPrice] = useState("");
  const [superPw, setSuperPw] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editingPrice, setEditingPrice] = useState(false);

  const submitPriceEdit = async () => {
    const p = parsePrice(newPrice);
    if (!p || p <= 0) return toast.error("Geçerli bir fiyat girin");
    const priv = hasPrivilege();
    if (!priv && !superPw) return toast.error("Süper şifreyi girin");
    setEditingPrice(true);
    try {
      const payload = { price: p, note: editNote };
      if (priv) payload.privilege_token = getPrivilegeToken();
      else payload.super_password = superPw;
      await api.patch(`/offers/${priceEdit.offer.id}/admin-price`, payload);
      toast.success("Teklif fiyatı güncellendi");
      setPriceEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEditingPrice(false);
    }
  };

  const remindAll = async () => {
    setRemindingAll(true);
    try {
      const { data } = await api.post(`/requests/${id}/remind-all`);
      setReq((r) => ({ ...r, columns: r.columns.map((c) => (data.sent[c.supplier.id] ? { ...c, reminded_at: data.sent[c.supplier.id], reminder_count: (c.reminder_count || 0) + 1 } : c)) }));
      if (data.sent_count > 0) toast.success(`${data.sent_count} tedarikçiye hatırlatma gönderildi`);
      if (data.failed.length > 0) toast.error(`Gönderilemedi: ${data.failed.join(", ")}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemindingAll(false);
    }
  };

  const remind = async (sid) => {
    setReminding(sid);
    try {
      const { data } = await api.post(`/requests/${id}/remind/${sid}`);
      setReq((r) => ({ ...r, columns: r.columns.map((c) => (c.supplier.id === sid ? { ...c, reminded_at: data.sent_at, reminder_count: (c.reminder_count || 0) + 1 } : c)) }));
      toast.success("Hatırlatma e-postası gönderildi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReminding(null);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/requests/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cop31_${(req?.title || "karsilastirma").replace(/[^\p{L}\p{N} _-]/gu, "").trim().replace(/\s+/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel dosyası oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/requests/${id}`).then((r) => setReq(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  }, [load]);

  const toTRY = useCallback(
    (offer) => {
      if (!offer) return null;
      if (offer.currency === "TRY") return offer.price;
      const rate = rates?.[offer.currency];
      return rate ? offer.price * rate : null;
    },
    [rates]
  );

  const act = async (offerId, action) => {
    const priv = hasPrivilege();
    if (action === "accept" && !isCustomer && !priv && !actPw) return toast.error("Kabul işlemi için süper şifre gerekli");
    try {
      const payload = { note };
      if (action === "accept" && !isCustomer) {
        if (priv) payload.privilege_token = getPrivilegeToken();
        else payload.super_password = actPw;
      }
      await api.post(`/offers/${offerId}/${action}`, payload);
      toast.success(action === "accept" ? "Teklif kabul edildi, tedarikçiler e-posta ile bilgilendirildi" : "Teklif reddedildi, tedarikçi bilgilendirildi");
      setDetail(null);
      setNote("");
      setActPw("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [noBidBusy, setNoBidBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteList, setInviteList] = useState([]);
  const [inviteSel, setInviteSel] = useState([]);
  const [inviteQ, setInviteQ] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const openInvite = async () => {
    setInviteSel([]);
    setInviteQ("");
    setInviteOpen(true);
    try {
      const r = await api.get("/users");
      const existing = new Set((req?.columns || []).map((c) => c.supplier.id));
      setInviteList((r.data || []).filter((u) => u.role === "supplier" && u.is_active !== false && !existing.has(u.id)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const doInvite = async () => {
    setInviteBusy(true);
    try {
      const r = await api.post(`/requests/${id}/invite-suppliers`, { supplier_ids: inviteSel });
      toast.success(`${r.data.added} firma davet edildi${r.data.late_allowed ? " (süre dolmuş olsa da teklif verebilecekler)" : ""}`);
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setInviteBusy(false);
    }
  };
  const [noBidConfirm, setNoBidConfirm] = useState(null);
  const [revokeFor, setRevokeFor] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const doRevoke = async () => {
    if (!revokeFor?.offer) return;
    setRevokeBusy(true);
    try {
      await api.post(`/offers/${revokeFor.offer.id}/revoke-accept`);
      toast.success("Kazanan statüsü iptal edildi — firmaya e-posta gönderildi");
      setRevokeFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRevokeBusy(false);
    }
  };
  const noBidReject = (supplierIds) => setNoBidConfirm(supplierIds);
  const doNoBidReject = async () => {
    const ids = noBidConfirm;
    setNoBidConfirm(null);
    setNoBidBusy(true);
    try {
      const r = await api.post(`/requests/${id}/no-bid-reject`, { supplier_ids: ids });
      toast.success(`${r.data.count} tedarikçi reddedildi, bilgilendirme e-postası gönderildi`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setNoBidBusy(false);
    }
  };

  const noBidUndo = async (supplierId) => {
    try {
      await api.post(`/requests/${id}/no-bid-reject/undo`, { supplier_id: supplierId });
      toast.success("Red geri alındı, tedarikçi yeniden değerlendirmede");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const undo = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/undo-reject`);
      toast.success("Karar geri alındı, teklif yeniden değerlendirmede");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const { sorted, bestSupplierId } = useMemo(() => {
    if (!req) return { sorted: [], bestSupplierId: null };
    const cols = [...req.columns];
    const withOffers = cols.filter((c) => c.offer);
    let best = null;
    const allConvertible = withOffers.length > 0 && withOffers.every((c) => toTRY(c.offer) !== null);
    if (allConvertible) {
      best = withOffers.reduce((a, b) => (toTRY(a.offer) <= toTRY(b.offer) ? a : b), withOffers[0]);
    } else if (withOffers.length > 0 && withOffers.every((c) => c.offer.currency === withOffers[0].offer.currency)) {
      best = withOffers.reduce((a, b) => (a.offer.price <= b.offer.price ? a : b), withOffers[0]);
    }
    const apMap = req.pending_approval_offers || {};
    cols.sort((a, b) => {
      const aw = a.offer?.status === "accepted" ? 1 : 0;
      const bw = b.offer?.status === "accepted" ? 1 : 0;
      if (aw !== bw) return bw - aw;
      const aa = a.offer && apMap[a.offer.id] ? 1 : 0;
      const ba = b.offer && apMap[b.offer.id] ? 1 : 0;
      if (aa !== ba) return ba - aa;
      if (!a.offer && !b.offer) return 0;
      if (!a.offer) return 1;
      if (!b.offer) return -1;
      if (sortBy === "date") return new Date(b.offer.created_at) - new Date(a.offer.created_at);
      const av = toTRY(a.offer) ?? a.offer.price;
      const bv = toTRY(b.offer) ?? b.offer.price;
      return sortBy === "price_desc" ? bv - av : av - bv;
    });
    return { sorted: cols, bestSupplierId: withOffers.length > 0 ? best?.supplier.id : null };
  }, [req, sortBy, toTRY]);

  const visibleCols = useMemo(() => {
    const q = trFold(supplierSearch.trim());
    if (!q) return sorted;
    return sorted.filter((c) => trFold(c.supplier.company_name).includes(q));
  }, [sorted, supplierSearch]);

  if (!req) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const fmtTL = (v) => v.toLocaleString("tr-TR", { maximumFractionDigits: 0 });

  return (
    <div className="space-y-8 fade-in">
      <div>
        <Link to={isCustomer ? "/musteri/teklifler" : "/admin/teklifler"} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-requests">
          <ArrowLeft size={15} /> Tüm Tekliflere Dön
        </Link>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Teklif Karşılaştırma</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="compare-title">{req.title}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
            {req.project && <span data-testid="compare-project-info">{projectInfo(req.project)}</span>}
            <span>Son tarih: {deadlineLabel(req)}</span>
            <DeadlineWarning deadline={req.deadline} time={req.deadline_time} />
          </div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-5 space-y-3 max-w-3xl">
        <div className="text-xs uppercase tracking-wider text-slate-500">Talep Ekleri</div>
        <FileChips files={req.files} />
      </div>

      {(req.included_services?.length > 0 || req.excluded_services?.length > 0) && (
        <div className="max-w-3xl">
          <ServiceLists included={req.included_services} excluded={req.excluded_services} />
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-xs text-slate-500" data-testid="rates-info">
          {rates?.USD ? <>Güncel kur: 1 USD ≈ {rates.USD.toLocaleString("tr-TR")} TL · 1 EUR ≈ {rates.EUR.toLocaleString("tr-TR")} TL</> : "Kur bilgisi alınamadı"}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              placeholder="Tedarikçi ara..."
              data-testid="compare-supplier-search"
              className="rounded-none pl-8 pr-8 h-9 w-48 bg-white border-slate-200"
            />
            <SearchClear value={supplierSearch} onClear={() => setSupplierSearch("")} testid="compare-search-clear" />
          </div>
          <Button variant="outline" onClick={exportExcel} disabled={exporting} data-testid="compare-export-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 h-9 transition-colors duration-200">
            <FileSpreadsheet size={15} /> {exporting ? "Hazırlanıyor..." : "Excel'e Aktar"}
          </Button>
          {!isCustomer && !req.expired && req.columns.some((c) => !c.offer) && (
            <Button
              variant="outline"
              onClick={remindAll}
              disabled={remindingAll}
              data-testid="remind-all-button"
              className="rounded-none border-amber-500/40 bg-amber-50 text-amber-700 hover:bg-amber-100 gap-2 h-9 transition-colors duration-200"
            >
              <BellRing size={14} /> {remindingAll ? "Gönderiliyor..." : `Tümüne Uyarı Gönder (${req.columns.filter((c) => !c.offer).length})`}
            </Button>
          )}
          {!isCustomer && req.expired && req.columns.filter((c) => !c.offer && c.status === "suresi_doldu").length > 0 && (
            <Button
              variant="outline"
              onClick={() => noBidReject(req.columns.filter((c) => !c.offer && c.status === "suresi_doldu").map((c) => c.supplier.id))}
              disabled={noBidBusy}
              data-testid="no-bid-reject-all-button"
              className="rounded-none border-red-300 bg-red-50 text-red-600 hover:bg-red-100 gap-2 h-9 transition-colors duration-200"
            >
              <XCircle size={14} /> {noBidBusy ? "Reddediliyor..." : `Teklif Vermeyenleri Reddet (${req.columns.filter((c) => !c.offer && c.status === "suresi_doldu").length})`}
            </Button>
          )}
          {user?.role === "superadmin" && req.expired && (
            <Button
              variant="outline"
              onClick={openInvite}
              data-testid="invite-extra-supplier-button"
              className="rounded-none border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 gap-2 h-9 transition-colors duration-200"
            >
              <UserPlus size={14} /> Ek Firma Davet Et
            </Button>
          )}
          <Select value={sortBy} onValueChange={(v) => { if (v) setSortBy(v); }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-9 w-56" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              <SelectItem value="price" data-testid="sort-price">Fiyata göre (TL, artan)</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">Fiyata göre (TL, azalan)</SelectItem>
              <SelectItem value="date" data-testid="sort-date">Teklif tarihine göre (yeni)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {visibleCols.length === 0 && (
          <p className="col-span-full border border-slate-200 bg-white p-6 text-sm text-slate-500 text-center" data-testid="compare-search-empty">Aramanızla eşleşen tedarikçi bulunamadı.</p>
        )}
        {visibleCols.map((c) => {
          const tl = toTRY(c.offer);
          const isAccepted = c.offer?.status === "accepted";
          const anyAccepted = sorted.some((x) => x.offer?.status === "accepted");
          const isBest = c.supplier.id === bestSupplierId && !anyAccepted;
          const prop = c.offer ? proposalByOffer[c.offer.id] : null;
          const hasProposal = !isCustomer && prop && ["pending", "supplier_accepted", "supplier_countered"].includes(prop.status);
          return (
            <button
              key={c.supplier.id}
              onClick={() => setDetail(c)}
              data-testid={`compare-card-${c.supplier.id}`}
              className={`relative text-left border p-5 transition-colors duration-200 space-y-3 ${
                isAccepted
                  ? "border-emerald-500/60 bg-[#edf6e3] hover:bg-[#e3f0d6]"
                  : hasProposal
                    ? `bg-[#fdf6e9] hover:bg-[#fcefd8] ${isBest ? "border-emerald-500/50" : "border-amber-400/60"}`
                    : isBest ? "bg-white hover:bg-slate-50 border-emerald-500/50" : "bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-400"
              }`}
            >
              {hasProposal && !isAccepted && (
                <div className="absolute -top-3 right-4 flex items-center gap-1.5 bg-[#f6dfae] text-amber-900 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1" data-testid={`proposal-card-badge-${c.supplier.id}`}>
                  <HandCoins size={11} /> Fiyat Önerildi
                </div>
              )}
              {isAccepted && (
                <div className="absolute -top-3 left-4 flex items-center gap-1.5 bg-[#cde8ae] text-slate-900 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1" data-testid={`winner-badge-${c.supplier.id}`}>
                  <Award size={11} /> Kazanan Teklif
                </div>
              )}
              {isBest && (
                <div className="absolute -top-3 left-4 flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1" data-testid={`best-badge-${c.supplier.id}`}>
                  <Award size={11} /> En İyi Teklif
                </div>
              )}
              {isAccepted && (() => {
                const pt = transfers.find((t) => t.status === "pending_approval");
                if (!pt) return null;
                const aps = pt.approvers?.length ? pt.approvers : [{ name: pt.approver_name, email: pt.approver_email, status: "pending", reminder_count: pt.reminder_count || 0 }];
                const done = aps.filter((a) => a.status === "approved").length;
                const dt = (iso) => new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                return (
                  <div className="border border-amber-300 bg-amber-50/90 px-3 py-2.5 space-y-1.5" data-testid={`transfer-process-${c.supplier.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-700">
                        <Undo2 size={11} /> Devir Onayda → {pt.to_company}
                      </span>
                      <span className="text-[10px] font-bold text-amber-700 shrink-0" data-testid={`transfer-process-count-${c.supplier.id}`}>{done}/{aps.length} onay</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
                        <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                        <span>Devir başlatıldı — {pt.created_by_name} · {dt(pt.created_at)}</span>
                      </div>
                      {aps.map((a) => (
                        <div key={a.email} className="flex items-center gap-1.5 text-[11px]" data-testid={`transfer-process-step-${a.email}`}>
                          {a.status === "approved" ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0" /> : <Clock size={11} className="text-amber-500 shrink-0" />}
                          <span className={a.status === "approved" ? "text-emerald-700" : "text-amber-700"}>
                            {a.name || a.email} — {a.status === "approved"
                              ? `onayladı${a.decided_at ? ` · ${dt(a.decided_at)}` : ""}`
                              : `bekleniyor${a.reminder_count > 0 ? ` (${a.reminder_count} hatırlatma)` : ""}`}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                        <Clock size={11} className="shrink-0" />
                        <span>Tüm onaylar tamamlanınca devir otomatik uygulanacak</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-start justify-between gap-2">
                <div className="font-heading font-bold text-lg tracking-tight leading-tight" data-testid={`card-supplier-${c.supplier.id}`}>{c.supplier.company_name}</div>
                <div className="flex flex-col items-end gap-1">
                  {(!c.offer || c.offer.status !== "pending") && (
                    <StatusBadge status={c.offer ? c.offer.status : c.status} />
                  )}
                  {c.offer && c.offer.status === "pending" && (req.pending_approval_offers || {})[c.offer.id] && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-violet-100 border border-violet-300 text-violet-700 px-1.5 py-0.5"
                          title={`Onay bekleniyor: ${req.pending_approval_offers[c.offer.id].join(", ")}`}
                          data-testid={`pending-approval-badge-${c.supplier.id}`}>
                      <ShieldCheck size={9} /> Onayda: {req.pending_approval_offers[c.offer.id].join(", ")}
                    </span>
                  )}
                  {(() => {
                    const at = transfers.find((t) => t.status === "applied" || t.status === "approved");
                    if (!at || !c.offer) return null;
                    if (isAccepted && at.to_offer_id === c.offer.id)
                      return (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 border border-amber-300 text-amber-700 px-1.5 py-0.5" title={`${at.from_company} firmasından devralındı`} data-testid={`transferred-in-badge-${c.supplier.id}`}>
                          <Undo2 size={9} /> Devirle Kazandı
                        </span>
                      );
                    if (c.offer.status === "rejected" && at.from_offer_id === c.offer.id)
                      return (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-300 text-slate-500 px-1.5 py-0.5" title={`İhale ${at.to_company} firmasına devredildi`} data-testid={`transferred-out-badge-${c.supplier.id}`}>
                          <Undo2 size={9} /> Devredildi
                        </span>
                      );
                    return null;
                  })()}
                  {fullAdmin && isAccepted && sorted.filter((x) => x.offer && x.offer.status !== "accepted").length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setTransferOpen(true); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setTransferOpen(true); } }}
                      title="İhaleyi başka firmaya devret"
                      data-testid={`transfer-badge-${c.supplier.id}`}
                      className="flex items-center gap-1 border border-slate-400 bg-white text-slate-700 hover:bg-slate-100 text-[10px] font-semibold px-2 py-0.5 cursor-pointer transition-colors duration-200"
                    >
                      <Undo2 size={11} /> Devret
                    </span>
                  )}
                  {isAccepted && !isCustomer && user?.role === "superadmin" && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setRevokeFor(c); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setRevokeFor(c); } }}
                      title="Kazanan statüsünü iptal et"
                      data-testid={`revoke-accept-badge-${c.supplier.id}`}
                      className="flex items-center gap-1 border border-red-300 bg-white text-red-600 hover:bg-red-50 text-[10px] font-semibold px-2 py-0.5 cursor-pointer transition-colors duration-200"
                    >
                      <XCircle size={11} /> Statü İptali
                    </span>
                  )}
                  {c.offer?.customer_preferred && (
                    <span className="flex items-center gap-1 bg-blue-600 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5" data-testid={`customer-preferred-${c.supplier.id}`}>
                      <Star size={10} /> Müşteri Tercihi
                    </span>
                  )}
                  {c.unread_messages > 0 && (
                    <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`compare-unread-${c.supplier.id}`}>{c.unread_messages} yeni mesaj</span>
                  )}
                  {c.offer && !isCustomer && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); openQuickQ(c); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openQuickQ(c); } }}
                      title="Bu tedarikçiye hızlı soru sor"
                      data-testid={`quick-question-badge-${c.supplier.id}`}
                      className="flex items-center gap-1 border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[10px] font-semibold px-2 py-0.5 cursor-pointer transition-colors duration-200"
                    >
                      <MessageCircleQuestion size={11} /> Soru Sor
                    </span>
                  )}
                  {c.offer && !isCustomer && c.offer.status === "pending" && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setProposalFor(c); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setProposalFor(c); } }}
                      title="Bu tedarikçiye fiyat öner"
                      data-testid={`price-proposal-badge-${c.supplier.id}`}
                      className="flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 text-[10px] font-semibold px-2 py-0.5 cursor-pointer transition-colors duration-200"
                    >
                      <HandCoins size={11} /> Fiyat Öner
                    </span>
                  )}
                  {c.offer && !isCustomer && proposalByOffer[c.offer.id] && (() => {
                    const p = proposalByOffer[c.offer.id];
                    if (p.status === "pending")
                      return <span className="flex items-center gap-1 border border-amber-300 bg-amber-100/60 text-amber-700 text-[10px] font-semibold px-2 py-0.5" data-testid={`proposal-status-${c.supplier.id}`}><Clock size={10} /> Öneri: {p.proposed_price.toLocaleString("tr-TR")} {p.currency}</span>;
                    if (p.status === "supplier_accepted" || p.status === "supplier_countered")
                      return (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setApplyProposal({ ...p, company: c.supplier.company_name }); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setApplyProposal({ ...p, company: c.supplier.company_name }); } }}
                          title={p.status === "supplier_countered" ? "Tedarikçi farklı tutar önerdi — değerlendirmek için tıklayın" : "Tedarikçi kabul etti — son onay için tıklayın"}
                          data-testid={`proposal-status-${c.supplier.id}`}
                          className={`flex items-center gap-1 text-white text-[10px] font-bold px-2 py-0.5 cursor-pointer transition-colors duration-200 ${p.status === "supplier_countered" ? "bg-blue-600 hover:bg-blue-500" : "bg-emerald-600 hover:bg-emerald-500"}`}
                        >
                          {p.status === "supplier_countered"
                            ? <><HandCoins size={10} /> Karşı Öneri: {p.counter_price?.toLocaleString("tr-TR")} {p.currency}</>
                            : <><CheckCircle2 size={10} /> Kabul Etti — Son Onay</>}
                        </span>
                      );
                    if (p.status === "supplier_rejected")
                      return <span className="flex items-center gap-1 border border-red-300 bg-red-50 text-red-500 text-[10px] font-semibold px-2 py-0.5" data-testid={`proposal-status-${c.supplier.id}`}><XCircle size={10} /> Öneri Reddedildi</span>;
                    if (p.status === "admin_rejected")
                      return <span className="flex items-center gap-1 border border-slate-300 bg-slate-50 text-slate-500 text-[10px] font-semibold px-2 py-0.5" data-testid={`proposal-status-${c.supplier.id}`}><XCircle size={10} /> Karşı Öneri Reddedildi</span>;
                    if (p.status === "applied")
                      return <span className="flex items-center gap-1 border border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] font-semibold px-2 py-0.5" data-testid={`proposal-status-${c.supplier.id}`}><CheckCircle2 size={10} /> Yeni Fiyat Uygulandı</span>;
                    return null;
                  })()}
                  {c.viewed_at && !c.offer && (
                    <span className="flex items-center gap-1 border border-slate-300 bg-slate-50 text-slate-500 text-[10px] px-1.5 py-0.5" title={`Talebi görüntüledi: ${new Date(c.viewed_at).toLocaleString("tr-TR")}`} data-testid={`viewed-badge-${c.supplier.id}`}>
                      <Eye size={10} /> Görüntüledi
                    </span>
                  )}
                </div>
              </div>
              {c.offer ? (
                <div>
                  <div className={`font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2 ${isBest ? "text-emerald-600" : "text-slate-900"}`} data-testid={`card-price-${c.supplier.id}`}>
                    <span>{c.offer.price.toLocaleString("tr-TR")} <span className="text-sm font-medium text-slate-500">{c.offer.currency}</span></span>
                    {canEditPrice && (
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setPriceEdit(c); setNewPrice(String(c.offer.price)); setSuperPw(""); setEditNote(""); } }}
                        onClick={(e) => { e.stopPropagation(); setPriceEdit(c); setNewPrice(String(c.offer.price)); setSuperPw(""); setEditNote(""); }}
                        title="Fiyatı düzenle (süper şifre gerekir)"
                        data-testid={`edit-price-button-${c.supplier.id}`}
                        className="text-slate-300 hover:text-slate-600 cursor-pointer transition-colors duration-200"
                      >
                        <Pencil size={14} />
                      </span>
                    )}
                  </div>
                  {c.offer.currency !== "TRY" && tl !== null && (
                    <div className="text-xs text-slate-500 mt-1" data-testid={`card-tl-${c.supplier.id}`}>≈ {fmtTL(tl)} TL</div>
                  )}
                  <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-1.5" data-testid={`offer-date-${c.supplier.id}`}>
                    <Clock size={10} /> Teklif: {new Date(c.offer.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="font-heading font-extrabold text-3xl tracking-tight text-slate-300">—</div>
                  {req.created_at && waitDur(req.created_at, now) && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400" data-testid={`waiting-time-${c.supplier.id}`}>
                      <Clock size={10} /> Sistemde {waitDur(req.created_at, now)} bekliyor
                    </div>
                  )}
                  {!req.expired && !isCustomer && (
                    <span
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); remind(c.supplier.id); } }}
                      onClick={(e) => { e.stopPropagation(); if (reminding !== c.supplier.id) remind(c.supplier.id); }}
                      title={c.viewed_at ? "Görüntülediği halde teklif vermedi uyarısı gönder" : "Talebi görüntülemedi uyarısı gönder"}
                      data-testid={`remind-button-${c.supplier.id}`}
                      className={`inline-flex items-center gap-1.5 border text-[11px] px-2.5 py-1.5 cursor-pointer transition-colors duration-200 ${
                        reminding === c.supplier.id ? "opacity-50 pointer-events-none" : ""
                      } border-amber-500/40 bg-amber-50 text-amber-700 hover:bg-amber-100`}
                    >
                      <BellRing size={11} /> {reminding === c.supplier.id ? "Gönderiliyor..." : "Uyarı Gönder"}
                    </span>
                  )}
                  {c.reminded_at && (
                    <div className="text-[10px] text-slate-400" data-testid={`reminded-at-${c.supplier.id}`}>
                      Hatırlatıldı{c.reminder_count > 1 ? ` (${c.reminder_count} kez)` : ""}: {new Date(c.reminded_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                  {c.status === "suresi_doldu" && fullAdmin && (
                    <span
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setManualFor(c); setManualPrice(""); setManualNote(""); setManualPw(""); setManualNotify(true); setManualMsgDirty(false); } }}
                      onClick={(e) => { e.stopPropagation(); setManualFor(c); setManualPrice(""); setManualNote(""); setManualPw(""); setManualNotify(true); setManualMsgDirty(false); }}
                      title="Bu tedarikçi adına fiyat gir (süper şifre gerekir)"
                      data-testid={`manual-offer-button-${c.supplier.id}`}
                      className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 text-[11px] px-2.5 py-1.5 cursor-pointer transition-colors duration-200"
                    >
                      <Pencil size={11} /> Fiyat Gir
                    </span>
                  )}
                  {c.status === "suresi_doldu" && !isCustomer && (
                    <span
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); noBidReject([c.supplier.id]); } }}
                      onClick={(e) => { e.stopPropagation(); noBidReject([c.supplier.id]); }}
                      title="Teklif vermeyen bu tedarikçiyi reddet (bilgilendirme e-postası gönderilir)"
                      data-testid={`no-bid-reject-${c.supplier.id}`}
                      className={`inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 text-[11px] px-2.5 py-1.5 cursor-pointer transition-colors duration-200 ${noBidBusy ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      <XCircle size={11} /> Reddet
                    </span>
                  )}
                  {!c.offer && c.no_bid_rejected_at && !isCustomer && (
                    <span
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); noBidUndo(c.supplier.id); } }}
                      onClick={(e) => { e.stopPropagation(); noBidUndo(c.supplier.id); }}
                      title="Reddi geri al"
                      data-testid={`no-bid-undo-${c.supplier.id}`}
                      className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 text-[11px] px-2.5 py-1.5 cursor-pointer transition-colors duration-200"
                    >
                      <Undo2 size={11} /> Reddi Geri Al
                    </span>
                  )}
                </div>
              )}
              {c.offer?.customer_preferred && c.offer.status === "pending" && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); if (isCustomer) act(c.offer.id, "accept"); else { setActPw(""); setDetail(c); } }}
                  data-testid={`quick-accept-preferred-${c.supplier.id}`}
                  className="flex items-center justify-center gap-1.5 w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-2 transition-colors duration-200"
                >
                  <CheckCircle2 size={13} /> Tercihi Kabul Et
                </span>
              )}
              <div className="text-xs text-slate-400">Detay için tıklayın</div>
            </button>
          );
        })}
      </div>

      {!isCustomer && transfers.length > 0 && (
        <div className="border border-slate-200 bg-white" data-testid="transfer-records">
          <div className="px-5 py-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">Devir Kayıtları</div>
          <div className="divide-y divide-slate-100">
            {transfers.map((t) => (
              <div key={t.id} className="px-5 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`transfer-record-${t.id}`}>
                <span className={`text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide ${t.status === "applied" || t.status === "approved" ? "bg-emerald-100 text-emerald-800" : t.status === "pending_approval" ? "bg-amber-100 text-amber-700" : "bg-red-50 text-red-600"}`}>
                  {t.status === "applied" ? "Uygulandı" : t.status === "approved" ? "Onaylandı" : t.status === "pending_approval" ? "Onay Bekliyor" : "Reddedildi"}
                </span>
                <span><strong>{t.from_company}</strong> ({t.from_price?.toLocaleString("tr-TR")} {t.from_currency}) → <strong>{t.to_company}</strong> ({t.to_price?.toLocaleString("tr-TR")} {t.to_currency})</span>
                <span className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {t.created_by_name}</span>
                {(t.status === "applied" || t.status === "approved") && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.get(`/requests/${id}/transfers/${t.id}/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `devir_tutanagi_${t.id.slice(0, 8)}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Tutanak PDF oluşturulamadı"); }
                    }}
                    data-testid={`transfer-pdf-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 transition-colors duration-150"
                  >
                    <FileSpreadsheet size={11} /> Tutanak PDF
                  </button>
                )}
                {t.note && <span className="text-xs text-slate-500 w-full">Not: {t.note}</span>}
                {(t.approvers || []).length > 0 && (
                  <div className="w-full flex flex-wrap items-center gap-2 pt-1" data-testid={`transfer-approvers-${t.id}`}>
                    {t.approvers.map((a) => (
                      <span key={a.email} className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border ${a.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : a.status === "declined" ? "bg-red-50 border-red-200 text-red-600" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid={`transfer-approver-chip-${t.id}-${a.email}`}>
                        {a.status === "approved" ? <CheckCircle2 size={11} /> : a.status === "declined" ? <XCircle size={11} /> : <Clock size={11} />}
                        <strong>{a.name || a.email}</strong>
                        {a.status === "approved" ? `onayladı · ${a.decided_at ? new Date(a.decided_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}${a.ip ? ` · IP: ${a.ip}` : ""}` : a.status === "declined" ? `reddetti${a.ip ? ` · IP: ${a.ip}` : ""}` : "bekleniyor"}
                        {a.status === "pending" && a.reminder_count > 0 && <span className="text-[10px] text-amber-500">({a.reminder_count} hatırlatma)</span>}
                      </span>
                    ))}
                    {t.status === "pending_approval" && t.approvers.some((a) => a.status === "pending") && (
                      <button onClick={() => openRemind(t)} data-testid={`transfer-remind-btn-${t.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 border border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors duration-150">
                        <Bell size={11} /> Hatırlatma Gönder
                      </button>
                    )}
                  </div>
                )}
                {!(t.approvers || []).length && t.status === "pending_approval" && t.approver_email && (
                  <button onClick={() => openRemind(t)} data-testid={`transfer-remind-btn-${t.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 border border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors duration-150">
                    <Bell size={11} /> Hatırlatma Gönder
                  </button>
                )}
                {t.approval && (
                  <span className="text-xs text-slate-500 w-full" data-testid={`transfer-approval-${t.id}`}>
                    Dijital onay kaydı: <strong>{t.approval.approver_name || t.approval.approver_email}</strong> ({t.approval.approver_email}) — {t.approval.decision === "approve" ? "Onayladı" : "Reddetti"} · {new Date(t.approval.decided_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}{t.approval.ip ? ` · IP: ${t.approval.ip}` : ""}{t.approval.decision_note ? ` · "${t.approval.decision_note}"` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!remindT} onOpenChange={(o) => { if (!o) setRemindT(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="transfer-remind-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Bell size={18} className="text-amber-600" /> Onay Hatırlatması Gönder
            </DialogTitle>
          </DialogHeader>
          {remindT && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <strong>{remindT.from_company}</strong> → <strong>{remindT.to_company}</strong> devri için kararı beklenen onaylayıcılar:
              </p>
              <div className="space-y-2">
                {((remindT.approvers || []).filter((a) => a.status === "pending").length
                  ? remindT.approvers.filter((a) => a.status === "pending").map((a) => ({ name: a.name, email: a.email, rc: a.reminder_count }))
                  : [{ name: remindT.approver_name, email: remindT.approver_email, rc: remindT.reminder_count || 0 }]
                ).map((a) => (
                  <label key={a.email} className="flex items-center gap-3 border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150" data-testid={`remind-approver-${a.email}`}>
                    <input
                      type="checkbox"
                      checked={remindSel.includes(a.email)}
                      onChange={(e) => setRemindSel((p) => (e.target.checked ? [...p, a.email] : p.filter((x) => x !== a.email)))}
                      className="accent-slate-900"
                    />
                    <span className="text-sm">
                      <strong>{a.name || a.email}</strong> <span className="text-slate-400 text-xs">({a.email})</span>
                      {a.rc > 0 && <span className="text-[11px] text-amber-600 ml-2">{a.rc} kez hatırlatıldı</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Ek Not (opsiyonel)</Label>
                <Textarea value={remindNote} onChange={(e) => setRemindNote(e.target.value)} rows={2} data-testid="remind-note-input" placeholder="Hatırlatma e-postasına eklenecek not..." className="rounded-none bg-white border-slate-200" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRemindT(null)} data-testid="remind-cancel" className="flex-1 rounded-none h-11 border-slate-200 hover:bg-slate-100 transition-colors duration-200">Vazgeç</Button>
                <Button onClick={sendRemind} disabled={remindBusy} data-testid="remind-send" className="flex-1 rounded-none h-11 bg-amber-600 hover:bg-amber-500 text-white gap-2 font-semibold transition-colors duration-200">
                  <Bell size={14} /> {remindBusy ? "Gönderiliyor..." : "Hatırlatma Gönder"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={(o) => { if (!o) setTransferOpen(false); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md max-h-[85vh] overflow-y-auto" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Undo2 size={18} className="text-slate-600" /> İhaleyi Devret
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Yeni Kazanan Firma</Label>
              <select value={tTo} onChange={(e) => setTTo(e.target.value)} data-testid="transfer-to-select" className="w-full h-11 px-3 text-sm border border-slate-200 bg-white focus:outline-none focus:border-slate-400">
                <option value="">Firma seçin...</option>
                {sorted.filter((x) => x.offer && x.offer.status !== "accepted").map((x) => (
                  <option key={x.offer.id} value={x.offer.id}>{x.supplier.company_name} — {x.offer.price.toLocaleString("tr-TR")} {x.offer.currency}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Devir Notu (opsiyonel)</Label>
              <Textarea value={tNote} onChange={(e) => setTNote(e.target.value)} rows={2} data-testid="transfer-note-input" className="rounded-none bg-white border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label>Onay Şekli</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setTMode("direct")} data-testid="transfer-mode-direct" className={`flex-1 text-xs font-semibold px-3 py-2 border transition-colors duration-150 ${tMode === "direct" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>Hemen Devret</button>
                <button type="button" onClick={() => setTMode("approval")} data-testid="transfer-mode-approval" className={`flex-1 text-xs font-semibold px-3 py-2 border transition-colors duration-150 ${tMode === "approval" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>Üçüncü Kişi Onayıyla</button>
              </div>
            </div>
            {tMode === "approval" && (
              <div className="space-y-2">
                {tApprovers.map((a, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1.2fr_auto] gap-2 items-end">
                    <div className="space-y-1.5">
                      {i === 0 && <Label>Onaylayıcı Adı</Label>}
                      <Input value={a.name} onChange={(e) => setTApprovers((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`transfer-approver-name-${i}`} className="rounded-none bg-white border-slate-200 h-10" />
                    </div>
                    <div className="space-y-1.5">
                      {i === 0 && <Label>Onaylayıcı E-posta *</Label>}
                      <Input value={a.email} onChange={(e) => setTApprovers((p) => p.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} type="email" data-testid={`transfer-approver-email-${i}`} className="rounded-none bg-white border-slate-200 h-10" />
                    </div>
                    {tApprovers.length > 1 ? (
                      <button type="button" onClick={() => setTApprovers((p) => p.filter((_, j) => j !== i))} data-testid={`transfer-approver-remove-${i}`} className="h-10 w-10 flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors duration-150">
                        <XCircle size={14} />
                      </button>
                    ) : <span className="w-10" />}
                  </div>
                ))}
                {tApprovers.length < 3 && (
                  <button type="button" onClick={() => setTApprovers((p) => [...p, { name: "", email: "" }])} data-testid="transfer-add-approver" className="text-xs font-semibold text-slate-600 border border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-3 py-2 w-full transition-colors duration-150">
                    + Onaylayıcı Ekle ({tApprovers.length}/3)
                  </button>
                )}
                <p className="text-xs text-slate-500">
                  {tApprovers.filter((a) => a.email.trim()).length > 1
                    ? "Birden fazla onaylayıcı girdiniz: devir ancak TÜM onaylayıcılar onayladığında uygulanır. Herhangi biri reddederse devir iptal olur."
                    : "Onaylayıcıya Magic Link e-postası gönderilir; kararı (ad, e-posta, tarih, IP) dijital olarak kayıt altına alınır. Devir ancak onaydan sonra uygulanır."}
                </p>
              </div>
            )}
            {hasPrivilege() ? (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="transfer-privilege-note">Özel Yetki aktif — süper şifre gerekmez. İşlem kayıt altına alınır.</p>
            ) : (
              <div className="space-y-1.5">
                <Label>Süper Şifre *</Label>
                <Input type="password" value={tPw} onChange={(e) => setTPw(e.target.value)} data-testid="transfer-super-password" className="rounded-none bg-white border-slate-200 h-10" />
              </div>
            )}
            <Button onClick={submitTransfer} disabled={tBusy} data-testid="transfer-submit" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold">
              {tBusy ? "İşleniyor..." : tMode === "approval" ? "Onaya Gönder" : "Devri Uygula"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="offer-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-bold tracking-tight">{detail.supplier.company_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">Durum</span>
                  <StatusBadge status={detail.offer && detail.offer.status !== "pending" ? detail.offer.status : detail.status} />
                </div>
                {detail.offer && detail.offer.status === "pending" && (req.pending_approval_offers || {})[detail.offer.id] && (
                  <div className="border border-violet-300 bg-violet-50 px-3 py-2.5 flex items-start gap-2" data-testid="detail-pending-approval">
                    <ShieldCheck size={13} className="text-violet-600 shrink-0 mt-0.5" />
                    <span className="text-xs text-violet-800">
                      <strong>Onayda</strong> — bu teklif <strong>{req.pending_approval_offers[detail.offer.id].join(", ")}</strong> onayında bekliyor.
                    </span>
                  </div>
                )}
                {(() => {
                  const at = transfers.find((t) => t.status === "applied" || t.status === "approved");
                  if (!at || !detail.offer) return null;
                  const when = at.applied_at || at.created_at;
                  const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
                  const dt = fmtDT(when);
                  const deciders = (at.approvers || []).filter((a) => a.status === "approved");
                  if (!deciders.length && at.approval && at.approval.decision === "approve")
                    deciders.push({ name: at.approval.approver_name, email: at.approval.approver_email, decided_at: at.approval.decided_at, ip: at.approval.ip });
                  const approverList = deciders.length > 0 && (
                    <span className="block mt-1.5 pt-1.5 border-t border-amber-200/70" data-testid="detail-transfer-approvers">
                      <span className="font-semibold">Devri Onaylayan{deciders.length > 1 ? "lar" : ""}:</span>
                      {deciders.map((a, i) => (
                        <span key={i} className="block mt-0.5" data-testid={`transfer-approver-${i}`}>
                          ✓ {a.name ? `${a.name} — ` : ""}{a.email}
                          <span className="block opacity-75 pl-3.5">{fmtDT(a.decided_at)}{a.ip ? ` · IP: ${a.ip}` : ""}</span>
                        </span>
                      ))}
                    </span>
                  );
                  if (detail.offer.status === "accepted" && at.to_offer_id === detail.offer.id)
                    return (
                      <div className="border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2" data-testid="detail-transferred-in">
                        <Undo2 size={13} className="text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-800">
                          <strong>Devirle Kazandı</strong> — bu ihale <strong>{at.from_company}</strong> firmasından devralındı.
                          <span className="block text-amber-600 mt-0.5">Kazanma tarihi: {dt}{at.created_by_name ? ` · Devri başlatan: ${at.created_by_name}` : ""}</span>
                          {approverList}
                        </span>
                      </div>
                    );
                  if (detail.offer.status === "rejected" && at.from_offer_id === detail.offer.id)
                    return (
                      <div className="border border-slate-300 bg-slate-50 px-3 py-2.5 flex items-start gap-2" data-testid="detail-transferred-out">
                        <Undo2 size={13} className="text-slate-500 shrink-0 mt-0.5" />
                        <span className="text-xs text-slate-600">
                          <strong>Devredildi</strong> — kazanan statüsü <strong>{at.to_company}</strong> firmasına devredildi.
                          <span className="block text-slate-400 mt-0.5">Devir tarihi: {dt}</span>
                          {approverList}
                        </span>
                      </div>
                    );
                  return null;
                })()}
                <div className="text-slate-500">
                  {detail.supplier.contact_name} · {detail.supplier.account_type}
                  <div className="text-xs text-slate-400">{detail.supplier.contact_email}</div>
                </div>
                {detail.offer ? (
                  <>
                    <div className="border border-slate-200 p-4">
                      <div className="text-xs uppercase tracking-wider text-slate-500">Fiyat</div>
                      <div className="font-heading font-extrabold text-4xl tracking-tight mt-1" data-testid="modal-price">
                        {detail.offer.price.toLocaleString("tr-TR")} <span className="text-base font-medium text-slate-500">{detail.offer.currency}</span>
                      </div>
                      {detail.offer.currency !== "TRY" && toTRY(detail.offer) !== null && (
                        <div className="text-sm text-slate-500 mt-1">≈ {fmtTL(toTRY(detail.offer))} TL</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Açıklama</div>
                      <p className="text-slate-600 whitespace-pre-line">{detail.offer.description || "—"}</p>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Teklif Dosyaları</div>
                      <FileChips files={detail.offer.files} />
                    </div>
                    <div className="text-xs text-slate-400">
                      Teklif tarihi: {formatDate(detail.offer.created_at)}
                      {detail.offer.updated_at && <> · Son revizyon: {formatDate(detail.offer.updated_at)}</>}
                    </div>
                    {(detail.offer.history || []).length > 0 && (
                      <div className="border border-slate-200 bg-[#F5F7FA] p-3 space-y-1.5" data-testid="modal-offer-history">
                        <div className="text-xs uppercase tracking-wider text-slate-500">Önceki Fiyatlar</div>
                        {[...detail.offer.history].reverse().map((h, i) => (
                          <div key={i} className="flex items-center justify-between text-sm text-slate-500">
                            <span className="line-through">{h.price.toLocaleString("tr-TR")} {h.currency}</span>
                            <span className="text-xs">{formatDate(h.revised_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(() => {
                      const offerProps = proposals.filter((p) => p.offer_id === detail.offer.id && p.status !== "superseded");
                      const allEdits = detail.offer.admin_price_edits || [];
                      const hist = detail.offer.history || [];
                      const edits = allEdits.filter((e) => !["Karşı tutar kabul edildi", "Fiyat önerisi tedarikçi tarafından kabul edildi", "Tedarikçi karşı teklifi", "Fiyat önerisi (anında uygulandı)"].includes(e.note || ""));
                      if (offerProps.length === 0 && edits.length === 0 && hist.length === 0) return null;
                      const events = [];
                      const cands = [];
                      if (allEdits.length > 0) cands.push([allEdits[0].at || "", allEdits[0].old_price]);
                      if (hist.length > 0) cands.push([hist[0].revised_at || "", hist[0].price]);
                      cands.sort((a, b) => (a[0] || "").localeCompare(b[0] || ""));
                      const firstPrice = cands.length > 0 ? cands[0][1] : detail.offer.price;
                      events.push({ t: detail.offer.created_at, color: "bg-slate-900", label: `${detail.offer.admin_entered ? "Sistemce girilen ilk fiyat" : "Tedarikçinin ilk teklifi"}: ${firstPrice.toLocaleString("tr-TR")} ${detail.offer.currency}` });
                      for (const e of edits) events.push({ t: e.at, color: "bg-slate-500", label: `${e.note ? `Fiyat güncellendi (${e.note})` : "Fiyat güncellendi"}: ${e.new_price.toLocaleString("tr-TR")} ${e.currency || detail.offer.currency}` });
                      hist.forEach((h, idx) => {
                        const newP = idx + 1 < hist.length ? hist[idx + 1].price : detail.offer.price;
                        const dir = newP > h.price ? "Tedarikçi fiyatı artırdı" : newP < h.price ? "Tedarikçi fiyatı düşürdü" : "Tedarikçi teklifini revize etti";
                        const color = newP > h.price ? "bg-red-500" : newP < h.price ? "bg-emerald-600" : "bg-slate-500";
                        events.push({ t: h.revised_at, color, label: `${dir}: ${h.price.toLocaleString("tr-TR")} → ${newP.toLocaleString("tr-TR")} ${detail.offer.currency}` });
                      });
                      for (const p of [...offerProps].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))) {
                        events.push({ t: p.created_at, color: "bg-amber-500", label: `${p.origin === "manual" ? "Komisyon tutarı iletildi" : p.origin === "target" ? "Hedef fiyat iletildi" : "Fiyat önerildi (güncel fiyat olarak uygulandı)"}: ${p.proposed_price.toLocaleString("tr-TR")} ${p.currency}` });
                        if (p.responded_at) {
                          if (p.counter_price != null) events.push({ t: p.responded_at, color: "bg-blue-600", label: `Tedarikçi karşı öneri verdi: ${p.counter_price.toLocaleString("tr-TR")} ${p.currency}${p.supplier_note ? ` — "${p.supplier_note}"` : ""}` });
                          else if (p.status === "supplier_rejected") events.push({ t: p.responded_at, color: "bg-red-500", label: `Tedarikçi reddetti${p.supplier_note ? ` — "${p.supplier_note}"` : ""}` });
                          else events.push({ t: p.responded_at, color: "bg-emerald-600", label: `Tedarikçi kabul etti${p.supplier_note ? ` — "${p.supplier_note}"` : ""}` });
                        }
                        if (p.applied_at) events.push({ t: p.applied_at, color: "bg-emerald-700", label: `Yeni fiyat uygulandı: ${(p.counter_price ?? p.proposed_price).toLocaleString("tr-TR")} ${p.currency}` });
                        if (p.rejected_at) events.push({ t: p.rejected_at, color: "bg-slate-500", label: "Yönetim karşı tutarı reddetti" });
                      }
                      events.sort((a, b) => (a.t || "").localeCompare(b.t || ""));
                      events.push({ t: null, color: "bg-emerald-600", label: `Güncel teklif fiyatı: ${detail.offer.price.toLocaleString("tr-TR")} ${detail.offer.currency}` });
                      return (
                        <div className="border border-slate-200 bg-white p-3" data-testid="negotiation-timeline">
                          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Pazarlık Geçmişi</div>
                          <div className="space-y-0">
                            {events.map((e, i) => (
                              <div key={i} className="flex gap-2.5">
                                <div className="flex flex-col items-center">
                                  <span className={`w-2 h-2 mt-1.5 shrink-0 ${e.color}`} />
                                  {i < events.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                                </div>
                                <div className="pb-3 min-w-0">
                                  <div className="text-sm text-slate-700">{e.label}</div>
                                  <div className="text-[11px] text-slate-400">{e.t ? new Date(e.t).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {detail.offer.status === "pending" && (
                      <div className="space-y-3 pt-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="decision-note" className="text-xs uppercase tracking-wider text-slate-500">Karar Notu (opsiyonel, e-postaya eklenir)</Label>
                          <Textarea id="decision-note" data-testid="decision-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="örn. Teslim süresi ve fiyat uygunluğu nedeniyle tercih edilmiştir..." className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm" />
                        </div>
                        {!isCustomer && !hasPrivilege() && (
                          <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-amber-700 flex items-center gap-1.5"><ShieldCheck size={12} /> Süper Şifre (kabul için gerekli)</Label>
                            <Input type="password" value={actPw} onChange={(e) => setActPw(e.target.value)} placeholder="Teklif kabulü süper şifre gerektirir" data-testid="accept-super-password-input" className="rounded-none border-amber-300 h-10 bg-amber-50/50" />
                          </div>
                        )}
                        {!isCustomer && hasPrivilege() && (
                          <p className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="accept-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez.</p>
                        )}
                        <div className="flex gap-2">
                          <Button onClick={() => act(detail.offer.id, "accept")} disabled={!isCustomer && !hasPrivilege() && !actPw} data-testid={`accept-offer-${detail.supplier.id}`} className="flex-1 rounded-none h-11 bg-emerald-600 hover:bg-emerald-500 text-white gap-2 font-semibold transition-colors duration-200">
                            <CheckCircle2 size={15} /> Kabul Et
                          </Button>
                          <Button variant="outline" onClick={() => act(detail.offer.id, "reject")} data-testid={`reject-offer-${detail.supplier.id}`} className="flex-1 rounded-none h-11 border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2 transition-colors duration-200">
                            <XCircle size={15} /> Reddet
                          </Button>
                        </div>
                      </div>
                    )}
                    {detail.offer.status === "rejected" &&
                      detail.offer.decided_at &&
                      Date.now() - new Date(detail.offer.decided_at).getTime() < 24 * 3600 * 1000 &&
                      !sorted.some((c) => c.offer?.status === "accepted") && (
                        <Button variant="outline" onClick={() => undo(detail.offer.id)} data-testid={`undo-reject-${detail.supplier.id}`} className="w-full rounded-none h-11 border-slate-200 hover:bg-slate-100 gap-2 font-semibold transition-colors duration-200">
                          <Undo2 size={15} /> Red Kararını Geri Al
                        </Button>
                      )}
                  </>
                ) : (
                  <p className="text-slate-500" data-testid="modal-no-offer">
                    {detail.status === "rejected"
                      ? `Teklif vermedi, komisyon tarafından reddedildi${detail.no_bid_rejected_at ? ` (${formatDate(detail.no_bid_rejected_at)})` : ""}.`
                      : detail.status === "suresi_doldu" ? "Süresi doldu, teklif vermedi." : "Henüz teklif vermedi."}
                  </p>
                )}
                {!isCustomer && <MessageThread endpoint={`/requests/${id}/messages/${detail.supplier.id}`} myRole="admin" title="Tedarikçi Mesajları" emptyText="Bu tedarikçiyle henüz mesajlaşma yok. İlk mesajı siz gönderebilirsiniz." placeholder="Tedarikçiye mesajınızı yazın..." />}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!priceEdit} onOpenChange={(o) => { if (!o) setPriceEdit(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="price-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <ShieldCheck size={18} className="text-amber-600" /> Teklif Fiyatını Düzenle
            </DialogTitle>
          </DialogHeader>
          {priceEdit && (
            <div className="space-y-4">
              <div className="border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="font-semibold">{priceEdit.supplier.company_name}</div>
                <div className="text-slate-500 text-xs mt-0.5">Mevcut fiyat: <span className="font-semibold text-slate-900">{priceEdit.offer.price.toLocaleString("tr-TR")} {priceEdit.offer.currency}</span></div>
              </div>
              <div className="space-y-1.5">
                <Label>Yeni Fiyat ({priceEdit.offer.currency})</Label>
                <Input type="text" inputMode="decimal" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="new-price-input" className="rounded-none border-slate-200 h-11" />
                {String(newPrice).trim() && !isNaN(parsePrice(newPrice)) && (
                  <p className="text-xs text-emerald-700 font-medium" data-testid="edit-price-preview">
                    = {parsePrice(newPrice).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {priceEdit.offer.currency}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Düzenleme Notu (opsiyonel)</Label>
                <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="Örn: Tedarikçi telefonla revize bildirdi" data-testid="edit-note-input" className="rounded-none border-slate-200 h-11" />
              </div>
              {hasPrivilege() ? (
                <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 border-t border-slate-200 pt-4" data-testid="price-edit-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez. İşlem kayıt altına alınır.</p>
              ) : (
                <div className="space-y-1.5 border-t border-slate-200 pt-4">
                  <Label className="flex items-center gap-1.5 text-amber-700"><ShieldCheck size={13} /> Süper Şifre</Label>
                  <Input type="password" value={superPw} onChange={(e) => setSuperPw(e.target.value)} placeholder="Bu işlem süper şifre gerektirir" data-testid="super-password-input" className="rounded-none border-amber-300 h-11 bg-amber-50/50" />
                  <p className="text-[11px] text-slate-500">Bu hassas işlem güvenlik amacıyla ek süper şifre doğrulaması gerektirir. İşlem kayıt altına alınır.</p>
                </div>
              )}
              <Button onClick={submitPriceEdit} disabled={editingPrice || !newPrice || (!hasPrivilege() && !superPw)} data-testid="price-edit-submit" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
                {editingPrice ? "Kaydediliyor..." : "Fiyatı Güncelle"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PriceProposalModal col={proposalFor} req={req || {}} onClose={() => setProposalFor(null)} onSent={loadProposals} />

      <Dialog open={!!manualFor} onOpenChange={(o) => { if (!o) setManualFor(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="manual-offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Pencil size={16} className="text-slate-600" /> Tedarikçi Adına Fiyat Gir
            </DialogTitle>
          </DialogHeader>
          {manualFor && (
            <div className="space-y-4">
              <div className="border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="font-semibold">{manualFor.supplier.company_name}</div>
                <div className="text-slate-500 text-xs mt-0.5">İhale süresi doldu, teklif vermedi. Girdiğiniz fiyat bu tedarikçi adına teklif olarak kaydedilir; sonrasında kabul ederek kazandırabilirsiniz.</div>
              </div>
              <div className="space-y-1.5">
                <Label>Fiyat ({req?.currency || "TRY"})</Label>
                <Input type="text" inputMode="decimal" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} data-testid="manual-price-input" className="rounded-none border-slate-200 h-11" />
                {String(manualPrice).trim() && !isNaN(parsePrice(manualPrice)) && (
                  <p className="text-xs text-emerald-700 font-medium">= {parsePrice(manualPrice).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {req?.currency || "TRY"}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Not (opsiyonel)</Label>
                <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Örn: Telefonla iletilen fiyat" data-testid="manual-note-input" className="rounded-none border-slate-200 h-11" />
              </div>
              <div className="space-y-2 border-t border-slate-200 pt-4">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
                  <input type="checkbox" checked={manualNotify} onChange={(e) => setManualNotify(e.target.checked)} data-testid="manual-notify-checkbox" className="h-4 w-4 accent-slate-900" />
                  Tedarikçiye onay e-postası gönder (Evet / Hayır)
                </label>
                {manualNotify && (
                  <div className="space-y-1.5">
                    <Textarea value={manualMsg} onChange={(e) => { setManualMsg(e.target.value); setManualMsgDirty(true); }} rows={8} data-testid="manual-message-input" className="rounded-none border-slate-200 text-sm" />
                    <p className="text-[11px] text-slate-500">Tedarikçi e-postadaki özel bağlantıdan tutarı kabul/ret edebilir veya farklı tutar önerebilir; yanıtı size onaya düşer.</p>
                  </div>
                )}
              </div>
              {hasPrivilege() ? (
                <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 border-t border-slate-200 pt-4" data-testid="manual-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez. İşlem kayıt altına alınır.</p>
              ) : (
                <div className="space-y-1.5 border-t border-slate-200 pt-4">
                  <Label className="flex items-center gap-1.5 text-amber-700"><ShieldCheck size={13} /> Süper Şifre</Label>
                  <Input type="password" value={manualPw} onChange={(e) => setManualPw(e.target.value)} placeholder="Bu işlem süper şifre gerektirir" data-testid="manual-super-password-input" className="rounded-none border-amber-300 h-11 bg-amber-50/50" />
                </div>
              )}
              <Button onClick={submitManualOffer} disabled={manualBusy || !manualPrice || (!hasPrivilege() && !manualPw)} data-testid="manual-offer-submit" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
                {manualBusy ? "Kaydediliyor..." : "Teklifi Kaydet"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ApplyProposalDialog proposal={applyProposal} company={applyProposal?.company} onClose={() => setApplyProposal(null)} onApplied={() => { loadProposals(); load(); }} />

      <Dialog open={!!noBidConfirm} onOpenChange={(o) => { if (!o) setNoBidConfirm(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="no-bid-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <XCircle size={18} className="text-red-600" /> Teklif Vermeyeni Reddet
            </DialogTitle>
          </DialogHeader>
          {noBidConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {noBidConfirm.length > 1
                  ? <>Süresi dolduğu halde teklif vermeyen <span className="font-semibold text-slate-900">{noBidConfirm.length} tedarikçi</span> reddedilecek ve her birine bilgilendirme e-postası gönderilecek.</>
                  : <>
                      <span className="font-semibold text-slate-900">
                        {req?.columns?.find((c) => c.supplier.id === noBidConfirm[0])?.supplier.company_name || "Tedarikçi"}
                      </span> reddedilecek ve bilgilendirme e-postası gönderilecek.
                    </>}
              </p>
              <p className="text-xs text-slate-500">Bu işlem kazanan seçimini etkilemez; gerekirse karttan "Reddi Geri Al" ile geri alabilirsiniz.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setNoBidConfirm(null)} data-testid="no-bid-confirm-cancel" className="flex-1 rounded-none h-11 border-slate-200 hover:bg-slate-100 transition-colors duration-200">
                  Vazgeç
                </Button>
                <Button onClick={doNoBidReject} data-testid="no-bid-confirm-submit" className="flex-1 rounded-none h-11 bg-red-600 hover:bg-red-500 text-white gap-2 font-semibold transition-colors duration-200">
                  <XCircle size={14} /> Reddet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeFor} onOpenChange={(o) => { if (!o) setRevokeFor(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="revoke-accept-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <XCircle size={18} className="text-red-600" /> Kazanan Statüsünü İptal Et
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              <strong>{revokeFor?.supplier?.company_name}</strong> firmasının kazanan statüsü kaldırılacak ve teklifi değerlendirme sürecine geri alınacak. Reddedilmiş diğer teklifler de yeniden değerlendirilebilir (kazandırılabilir) duruma döner. Yalnızca bu firmaya bilgilendirme e-postası gönderilecek; diğer firmalara e-posta gitmeyecek.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRevokeFor(null)} className="flex-1 rounded-none h-11 border-slate-200 hover:bg-slate-100 font-semibold">Vazgeç</Button>
              <Button onClick={doRevoke} disabled={revokeBusy} data-testid="revoke-accept-confirm" className="flex-1 rounded-none h-11 bg-red-600 text-white hover:bg-red-700 font-semibold gap-2 transition-colors duration-200">
                <XCircle size={14} /> {revokeBusy ? "İptal ediliyor..." : "Statüyü İptal Et"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="invite-extra-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <UserPlus size={18} className="text-blue-600" /> Ek Firma Davet Et
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500 -mt-1">
              Süresi dolmuş bu ihaleye davet edilen firmalar, süre kısıtı olmadan teklif verebilecek ve e-posta ile bilgilendirilecek.
            </p>
            <Input
              value={inviteQ}
              onChange={(e) => setInviteQ(e.target.value)}
              placeholder="Firma ara..."
              data-testid="invite-extra-search"
              className="rounded-none h-9 bg-[#F5F7FA] border-slate-200"
            />
            <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="invite-extra-list">
              {inviteList.filter((s) => !inviteQ.trim() || trFold(s.company_name || "").includes(trFold(inviteQ))).map((s) => (
                <label key={s.id} className="flex items-center gap-2.5 border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 transition-colors duration-150" data-testid={`invite-extra-item-${s.id}`}>
                  <input
                    type="checkbox"
                    checked={inviteSel.includes(s.id)}
                    onChange={(e) => setInviteSel((xs) => (e.target.checked ? [...xs, s.id] : xs.filter((x) => x !== s.id)))}
                  />
                  <span className="font-medium">{s.company_name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{s.contact_name}</span>
                </label>
              ))}
              {inviteList.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Davet edilebilecek firma yok</div>}
            </div>
            <Button onClick={doInvite} disabled={inviteBusy || inviteSel.length === 0} data-testid="invite-extra-submit" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
              <UserPlus size={14} /> {inviteBusy ? "Davet ediliyor..." : `Davet Et (${inviteSel.length})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quickQ} onOpenChange={(o) => { if (!o) setQuickQ(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg" data-testid="quick-question-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircleQuestion size={18} className="text-blue-600" /> Hızlı Soru Sor
            </DialogTitle>
          </DialogHeader>
          {quickQ && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 -mt-1">
                Soru yalnızca <span className="font-semibold text-slate-900">{quickQ.supplier.company_name}</span> firmasına gönderilir ve mesaj geçmişinde görünür.
              </p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="quick-question-list">
                {qqList.length === 0 && <div className="text-sm text-slate-400 py-3 text-center">Hazır soru yok. Ayarlar sayfasından ekleyebilirsiniz.</div>}
                {qqList.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => sendQuickQ(q.text)}
                    disabled={qqSending}
                    data-testid={`quick-question-item-${q.id}`}
                    className="w-full flex items-center justify-between gap-3 text-left border border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 px-3.5 py-2.5 text-sm transition-colors duration-200 group"
                  >
                    <span>{q.text}</span>
                    <Send size={13} className="shrink-0 text-slate-300 group-hover:text-blue-600 transition-colors duration-200" />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-400">
                <div className="h-px bg-slate-200 flex-1" /> veya serbest metin <div className="h-px bg-slate-200 flex-1" />
              </div>
              <Textarea value={qqText} onChange={(e) => setQqText(e.target.value)} rows={3} placeholder="Sorunuzu yazın..." data-testid="quick-question-textarea" className="rounded-none bg-[#F5F7FA] border-slate-200" />
              <Button onClick={() => sendQuickQ(qqText)} disabled={qqSending || !qqText.trim()} data-testid="quick-question-send-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
                <Send size={14} /> {qqSending ? "Gönderiliyor..." : "Soruyu Gönder"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
